/**
 * useReflection Hook
 *
 * React Query hook for fetching and managing reflection data
 * including the associated recording information.
 */

import { useQuery } from "@tanstack/react-query";
import { getReflectionWithRecording } from "@/services/reflections";
import type { ReflectionWithRecordingResponse } from "@/types/reflection";

interface UseReflectionResult {
  reflection: ReflectionWithRecordingResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useReflection(
  reflectionId: string | undefined
): UseReflectionResult {
  const { data, isLoading, error, refetch } = useQuery<
    ReflectionWithRecordingResponse,
    Error
  >({
    queryKey: ["reflection", reflectionId],
    queryFn: () => getReflectionWithRecording(reflectionId!),
    enabled: !!reflectionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    reflection: data || null,
    isLoading,
    error: error || null,
    refetch,
  };
}
